import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  countUnreadMessagesByPatient,
  listenForDoctorsList,
  listenForMessages,
  markMessagesAsSeen,
  sendMessage,
} from "../../Services/chatService";
import UserService from "../../Services/UserService";
import { DECREASECOUNT } from "../../Redux/actions/ChatActions";
import { useDebouncedValue, useLoggedInPatient } from "./chatHelpers";
import { useUnreadCounts, useResolvedDoctors, useConsultedDoctors } from "./useChatData";
import ConversedDoctorsList from "./ConversedDoctorsList";
import AllDoctorsList from "./AllDoctorsList";
import ChatWindow from "./ChatWindow";

// "Where did the user open this chat from" — drives back-navigation target.
const ORIGIN = {
  CONVERSED_LIST: "conversedList",
  ALL_DOCTORS_LIST: "allDoctorsList",
};

const VIDEO_DOCTOR_FILTER = (item) =>
  item.userType === "P" && item.videoFee && item.videoFee > 0;

async function fetchDoctorById(userId) {
  const response = await UserService.getDoctors({ userId, status: 1 });
  const matches = (response?.data || []).filter(VIDEO_DOCTOR_FILTER);
  return matches[0] ?? null;
}

function LiveChat() {
  const patient = useLoggedInPatient();
  const patientId = patient?.patientId;
  const dispatch = useDispatch();

  // --- view state ---
  const [view, setView] = useState(ORIGIN.CONVERSED_LIST); // 'conversedList' | 'allDoctorsList' | 'chat'
  const [chatOrigin, setChatOrigin] = useState(ORIGIN.CONVERSED_LIST);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [messages, setMessages] = useState([]);

  // --- conversed list state ---
  const [doctorIds, setDoctorIds] = useState([]);
  const [conversedSearch, setConversedSearch] = useState("");
  const unreadCounts = useUnreadCounts(patientId);
  const resolvedDoctors = useResolvedDoctors(doctorIds, fetchDoctorById);

  const visibleConversedDoctors = useMemo(() => {
    if (!conversedSearch) return resolvedDoctors;
    const key = conversedSearch.toLowerCase();
    return resolvedDoctors.filter((doc) => doc.name.toLowerCase().includes(key));
  }, [resolvedDoctors, conversedSearch]);

  // --- all-doctors list state ---
  const consulted = useConsultedDoctors(patientId);
  const [allDoctorsSearch, setAllDoctorsSearch] = useState("");
  const debouncedSearch = useDebouncedValue(allDoctorsSearch, 350);

  const messagesUnsubscribeRef = useRef(null);

  useEffect(() => {
    if (!patientId) return;
    const unsubscribe = listenForDoctorsList(patientId, setDoctorIds);
    return () => unsubscribe();
  }, [patientId]);

  // Note: filtering for the "all doctors" list is done client-side below
  // via visibleAllDoctors, using the debounced search term. If your backend
  // supports filtering getConsultedDoctors by name server-side, swap this
  // for a call to consulted.reset() with a name param instead — that would
  // scale better for a very large doctor list.


  const visibleAllDoctors = useMemo(() => {
    if (!debouncedSearch) return consulted.doctors;
    const key = debouncedSearch.toLowerCase();
    return consulted.doctors.filter((doc) => doc.name.toLowerCase().includes(key));
  }, [consulted.doctors, debouncedSearch]);

  useEffect(() => {
    return () => {
      messagesUnsubscribeRef.current?.();
    };
  }, []);

  const refreshUnreadBadgeCount = useCallback(async () => {
    if (!patientId) return;
    const counts = await countUnreadMessagesByPatient(patientId);
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    dispatch({ type: DECREASECOUNT, payload: total });
  }, [patientId, dispatch]);

  const openChat = useCallback(
    (doctor, origin) => {
      messagesUnsubscribeRef.current?.();

      setSelectedDoctor(doctor);
      setMessages([]);
      setChatOrigin(origin);
      setView("chat");

      markMessagesAsSeen(patientId, doctor.userId);
      refreshUnreadBadgeCount();

      messagesUnsubscribeRef.current = listenForMessages(
        patientId,
        doctor.userId,
        setMessages
      );
    },
    [patientId, refreshUnreadBadgeCount]
  );

  const handleSelectFromConversed = useCallback(
    (doctor) => openChat(doctor, ORIGIN.CONVERSED_LIST),
    [openChat]
  );

  const handleSelectFromAllDoctors = useCallback(
    (doctor) => openChat(doctor, ORIGIN.ALL_DOCTORS_LIST),
    [openChat]
  );

  // FIX: previously this always returned to the conversed list regardless
  // of where the chat was opened from. Now it returns to chatOrigin.
  const handleBack = useCallback(async () => {
    if (selectedDoctor) {
      markMessagesAsSeen(patientId, selectedDoctor.userId);
      await refreshUnreadBadgeCount();
    }
    setView(chatOrigin);
    setSelectedDoctor(null);
  }, [patientId, selectedDoctor, chatOrigin, refreshUnreadBadgeCount]);

  // FIX: previously called listenForMessages again after sending, leaking
  // a second pair of onSnapshot listeners every send. The existing
  // subscription (set up in openChat) already receives the new message
  // via Firestore's real-time update, so sending just needs to write.
  const handleSendMessage = useCallback(
    async (text) => {
      await sendMessage(patientId, selectedDoctor.userId, text);
    },
    [patientId, selectedDoctor]
  );

  const handleOpenAllDoctors = useCallback(() => {
    setView(ORIGIN.ALL_DOCTORS_LIST);
    if (consulted.doctors.length === 0) consulted.reset();
  }, [consulted]);

  const handleBackFromAllDoctors = useCallback(() => {
    setView(ORIGIN.CONVERSED_LIST);
  }, []);

  if (!patient) {
    return null; // not logged in / no patient context — nothing to render
  }

  if (view === "chat" && selectedDoctor) {
    return (
      <ChatWindow
        doctor={selectedDoctor}
        patientId={patientId}
        messages={messages}
        onBack={handleBack}
        onSendMessage={handleSendMessage}
      />
    );
  }

  if (view === ORIGIN.ALL_DOCTORS_LIST) {
    return (
      <AllDoctorsList
        doctors={visibleAllDoctors}
        isLoading={consulted.loading}
        error={consulted.error}
        sentinelRef={consulted.sentinelRef}
        onSelectDoctor={handleSelectFromAllDoctors}
        onBack={handleBackFromAllDoctors}
        onSearchChange={(e) => setAllDoctorsSearch(e.target.value)}
      />
    );
  }

  return (
    <ConversedDoctorsList
      doctors={visibleConversedDoctors}
      isLoading={resolvedDoctors.length === 0 && doctorIds.length > 0}
      unreadCounts={unreadCounts}
      onSelectDoctor={handleSelectFromConversed}
      onOpenAllDoctors={handleOpenAllDoctors}
      onSearchChange={(e) => setConversedSearch(e.target.value)}
    />
  );
}

export default LiveChat;